import { getSpeakerLabel, SpeakerType } from './utils';
import styles from './Transcript.module.scss';
import { forwardRef } from 'react';

type Brand<K, T> = K & { __brand: T };
export type Seconds = Brand<number, 'Seconds'>;

export type TranscriptionWithSpeaker = {
    participantInfo?: {
        identity: string;
    };
    relativeTime?: Seconds; // Start time of the speech segment
    endTime?: Seconds; // End time of the speech segment (when timestamp represents end)
    text: string;
    participantIdentity: string;
    speakerType: SpeakerType;
    timestamp: number;
    id: string;
};

type TranscriptProps = {
    transcriptions: TranscriptionWithSpeaker[];
    onClick?: (relativeTime: Seconds) => void;
    containerClassName?: string;
};

export const Transcripts = forwardRef<Map<string, HTMLElement>, TranscriptProps>(
    ({ transcriptions, onClick, containerClassName = styles.transcriptContainer }, ref) => {
        const getSpeakerClassName = (speakerType: SpeakerType): string => {
            switch (speakerType) {
                case 'user':
                    return styles.transcriptUser;
                case 'agent':
                    return styles.transcriptAgent;
                case 'ai':
                    return styles.transcriptAi;
                default:
                    return styles.transcriptUnknown;
            }
        };

        return (
            <div className={containerClassName}>
                {transcriptions.map((transcription) => (
                    <div
                        key={transcription.id}
                        className={`${styles.transcriptItem} ${getSpeakerClassName(transcription.speakerType)}`}
                        onClick={() => onClick?.(transcription.relativeTime || (0 as Seconds))}
                        ref={(el) => {
                            if (ref && typeof ref === 'object' && 'current' in ref) {
                                const map = ref.current;
                                if (map) {
                                    if (el) {
                                        map.set(transcription.id, el);
                                    } else {
                                        map.delete(transcription.id);
                                    }
                                }
                            }
                        }}
                    >
                        <div className={styles.transcriptHeader}>
                            <span className={styles.transcriptSpeaker}>
                                {getSpeakerLabel(transcription.speakerType)}
                            </span>
                            <span className={styles.transcriptIdentity}>({transcription.participantIdentity})</span>
                        </div>
                        <p className={styles.transcriptText}>{transcription.text}</p>
                    </div>
                ))}
            </div>
        );
    }
);

Transcripts.displayName = 'Transcripts';
